import ProductPageContainer from '@fogia/core-web/containers/ProductPageContainer';
import getExternalId from '@fogia/core-web/utils/getExternalId';
import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import CmsModules from '@fogia/core-web/libs/sanity/content/CmsModules';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { inServer } from '@fogia/core-web/constants';
import ContentGridWrapper from 'components/wrappers/ContentGridWrapper';
import ModuleGridWrapper from 'components/wrappers/ModuleGridWrapper';
import StickyBar from 'components/StickyBar';
import Breadcrumbs from 'components/Breadcrumbs';
import { show as showOverlay } from '@fogia/core-web/state/application/overlay';
import useAppDispatch from '@fogia/core-web/hooks/useAppDispatch';
import useIntersectionObserver from '@fogia/core-web/hooks/useIntersectionObserver';
import { isWideProduct } from 'Utils/isWideProduct';
import ProductGallery from './ProductGallery';
import BelowFold from './BelowFold';

const ProductPage = ({ product, selectedProduct, selectedProductVariants, variants, onVariantChange }) => {
    const wrapperRef = useRef({});
    const galleryRef = useRef(null);
    const dispatch = useAppDispatch();
    const pageContent = useAppSelector(({ page }) => page?.data?.pageContent, shallowEqual);
    const pageSlug = useAppSelector(({ page }) => page?.data?.slug);
    const [isGalleryInView, setIsGalleryInView] = useState(true);

    const handleGalleryIntersection = useCallback((entry) => {
        setIsGalleryInView(entry.isIntersecting);
    }, []);

    const [observe] = useIntersectionObserver(handleGalleryIntersection, {
        threshold: 0,
        rootMargin: '0px',
    });

    useEffect(() => {
        if (!inServer && galleryRef.current) {
            return observe(galleryRef.current);
        }
    }, [observe]);

    // Get the product ID - ProductPageContainer's setVariant expects this.state.product?.externalId
    const productId = getExternalId(product) || product?.id;

    const headerHeight =
        (!inServer && getComputedStyle(document?.documentElement).getPropertyValue('--header-height')) || 0;

    const scrollToTop = (smooth = false) => {
        if (wrapperRef && wrapperRef.current) {
            scrollTo({
                smooth,
                top: wrapperRef.current.offsetTop - headerHeight,
            });
        }
    };

    useEffect(() => {
        scrollToTop();
    }, []);

    // Get product info from API response
    const productName = product?.name || product?.fullName || '';
    const designerName = product?.designer?.name || product?.manufacturer?.name || '';
    const productDescriptionShort = product?.productDescriptionShort || '';
    const productDescriptionQuote = product?.quote || '';

    // Extract raw selected variant once (shared across multiple usages)
    const rawSelectedVariant = useMemo(() => {
        const values = selectedProductVariants ? Object.values(selectedProductVariants) : [];
        return values[0];
    }, [selectedProductVariants]);

    // Get main image from selected variant
    const selectedVariantPhotos = rawSelectedVariant?.product?.photosOnWhite || rawSelectedVariant?.photosOnWhite;
    const mainImage =
        (Array.isArray(selectedVariantPhotos) ? selectedVariantPhotos[0] : '') ||
        selectedProduct?.format?.image ||
        product?.format?.image ||
        (Array.isArray(product?.photosOnWhite) ? product.photosOnWhite[0] : '') ||
        '';
    // Files can be an object (with keys like 2dDwg, 3dDwg) or an array
    const productFiles = product?.files || variants?.[0]?.product?.files || {};
    const lineDrawings = !Array.isArray(productFiles) ? productFiles.lineDrawings : undefined;

    // Extract images from the SELECTED variant only (not all variants merged)
    const productImages = useMemo(() => {
        if (rawSelectedVariant) {
            const photos = rawSelectedVariant.product?.photosOnWhite || rawSelectedVariant.photosOnWhite;
            if (Array.isArray(photos) && photos.length > 0) {
                return photos;
            }
        }

        // Fallback: use first variant's photos
        if (variants?.[0]) {
            const photos = variants[0].product?.photosOnWhite || variants[0].photosOnWhite;
            if (Array.isArray(photos)) {
                return photos;
            }
        }

        return [];
    }, [rawSelectedVariant, variants]);

    const useImageFilenameAsAltText = (imageUrl) => {
        const filename = imageUrl.split('/').pop();
        const decodedFilename = decodeURIComponent(filename || '');
        return decodedFilename?.split('.').shift();
    };

    // Build galleryImages array for the ImageSlider from product imagery (lifestyle/studio photos)
    const productImagery = product?.imagery;
    const galleryImages = useMemo(() => {
        if (!Array.isArray(productImagery) || productImagery.length === 0) return [];

        return productImagery.map((url, index) => ({
            id: `imagery-${index}`,
            url,
            title: useImageFilenameAsAltText(url),
        }));
    }, [productImagery, productName]);

    // Get year from product
    const year = product?.year || product?.launchYear || '';

    // Get dimensions
    const dimensions = {
        height: product?.heightCm || product?.height,
        width: product?.widthCm || product?.width,
        depth: product?.depthCm || product?.depth,
    };

    // Derive selectedVariantId from rawSelectedVariant (the actual selected variant data)
    const selectedVariantId = rawSelectedVariant?.externalId || rawSelectedVariant?.product?.externalId || '';
    const selectedLooksValue = useMemo(() => {
        const finish = rawSelectedVariant?.product?.finish || rawSelectedVariant?.finish;
        if (finish) {
            return finish.replace(', ', ' / ');
        }
        return 'Select a look';
    }, [rawSelectedVariant]);

    // Derive configuration value from product family
    const configurationValue = product?.name || product?.fullName || '';

    // Handler to open the Configuration overlay
    const handleConfigurationClick = useCallback(() => {
        dispatch(
            showOverlay({
                view: 'configurationOverlay',
                transition: 'from-right',
                data: {
                    familyName: product?.family || '',
                    currentProductSlug: pageSlug || '',
                },
            }),
        );
    }, [dispatch, product?.family, pageSlug]);

    // Handler to open the Product Details overlay with the quotation tab
    const handleCtaClick = useCallback(() => {
        dispatch(
            showOverlay({
                view: 'productOverlay',
                transition: 'from-right',
                data: {
                    initialTab: 'quotation',
                },
            }),
        );
    }, [dispatch]);

    // Handler to open the Selected Looks overlay
    const handleSelectedLooksClick = useCallback(() => {
        dispatch(
            showOverlay({
                view: 'selectedLooksOverlay',
                transition: 'from-right',
                data: {
                    variants,
                    selectedVariantId,
                    productName,
                    onVariantSelect: (variant) => onVariantChange(productId, variant),
                },
            }),
        );
    }, [dispatch, variants, selectedVariantId, productName, onVariantChange, productId]);

    return (
        <div>
            <Breadcrumbs />
            <ContentGridWrapper ref={wrapperRef}>
                {/* Above the fold - Full viewport gallery */}
                <ModuleGridWrapper gridColumn="full-width" gridTemplateColumns="100%" p="0">
                    <ProductGallery
                        ref={galleryRef}
                        files={productFiles}
                        mainImage={mainImage}
                        productName={productName}
                        productImages={productImages}
                        text={productDescriptionQuote}
                        isWideProduct={isWideProduct(product)}
                    />
                </ModuleGridWrapper>

                {/* Below the fold sections */}
                <ModuleGridWrapper
                    gridColumn={['full-width', null, null, 'standard']}
                    gridTemplateColumns="100%"
                    p={['0 12px', null, null, '0']}
                >
                    <BelowFold
                        productName={productName}
                        designerName={designerName}
                        description={productDescriptionShort}
                        dimensions={dimensions}
                        lineDrawings={lineDrawings}
                        images={galleryImages}
                        year={year}
                        imageSliderHeading={product?.imageryTexts?.heading}
                        imageSliderText={product?.imageryTexts?.text}
                    />
                </ModuleGridWrapper>

                {/* Sticky bottom bar */}
                <StickyBar
                    selectedLooksValue={selectedLooksValue}
                    selectedLooksCount={variants?.length}
                    onSelectedLooksClick={handleSelectedLooksClick}
                    configurationValue={configurationValue}
                    onConfigurationClick={handleConfigurationClick}
                    onCtaClick={handleCtaClick}
                    isGalleryInView={isGalleryInView}
                />
            </ContentGridWrapper>

            {/* Page modules from CMS */}
            {pageContent && <CmsModules data={pageContent} />}
        </div>
    );
};

ProductPage.propTypes = {
    onVariantChange: PropTypes.func.isRequired,
    product: PropTypes.object.isRequired,
    selectedProduct: PropTypes.object.isRequired,
    selectedProductVariants: PropTypes.object.isRequired,
    variants: PropTypes.array.isRequired,
};

const ProductView = () => {
    const productExternalId = useAppSelector(({ page }) => page?.data?.externalId || page?.data?.product?.externalId);
    return <ProductPageContainer key={productExternalId} render={ProductPage} />;
};

export default ProductView;
